import React, { useEffect, useState } from "react";

import MuiDialog from "@mui/material/Dialog";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import { SKIP_REASON_OTHER, SKIP_REASONS } from "constants/batchPreference";

import styles from "./BatchPreference.module.scss";

const SKIP_REASON_ICONS = {
  NO_SUITABLE_SCHEDULE: CalendarMonthIcon,
  PREFERRED_START_DATE_UNAVAILABLE: EventAvailableIcon,
  CONFLICTS_WITH_ANOTHER_PROGRAM: SyncAltIcon,
  DECIDE_LATER: GroupsIcon,
  TIMETABLE_NOT_AVAILABLE: AccessTimeIcon,
  SALES_CALL_ENDED: PhoneInTalkIcon,
  TECHNICAL_ISSUE: WarningAmberIcon,
  [SKIP_REASON_OTHER]: MoreHorizIcon,
};

const SkipBatchModal = ({
  open,
  programName,
  isPending,
  onCancel,
  onConfirm,
}) => {
  const [reasonCode, setReasonCode] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  useEffect(() => {
    if (!open) {
      setReasonCode("");
      setAdditionalInfo("");
    }
  }, [open]);

  const isOther = reasonCode === SKIP_REASON_OTHER;
  const canConfirm = Boolean(reasonCode) && (!isOther || additionalInfo.trim());

  const handleClose = () => {
    if (isPending) return;
    setReasonCode("");
    setAdditionalInfo("");
    onCancel();
  };

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="skip-batching-title"
      PaperProps={{
        sx: {
          width: "560px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          borderRadius: "14px",
          overflow: "hidden",
        },
      }}
    >
      <div className={styles.confirmModal}>
        <div className={styles.confirmModalHeader}>
          <div className={styles.confirmModalTitleWrap}>
            <span className={styles.skipModalIconWrap}>
              <SendIcon className={styles.skipModalIcon} />
            </span>
            <h2 id="skip-batching-title" className={styles.confirmModalTitle}>
              Skip Batching
            </h2>
          </div>
          <button
            type="button"
            className={styles.confirmModalClose}
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className={styles.confirmModalBody}>
          <p className={styles.skipProgramName}>{programName}</p>
          <p className={styles.confirmGuidance}>
            Select a reason to skip this Program.
          </p>

          <div className={styles.skipReasonList}>
            {SKIP_REASONS.map((reason) => {
              const ReasonIcon =
                SKIP_REASON_ICONS[reason.value] || MoreHorizIcon;
              const isSelected = reasonCode === reason.value;

              return (
                <label
                  key={reason.value}
                  className={`${styles.skipReasonOption} ${
                    isSelected ? styles.skipReasonOptionSelected : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="skip-batch-reason"
                    value={reason.value}
                    checked={isSelected}
                    onChange={() => setReasonCode(reason.value)}
                  />
                  <ReasonIcon className={styles.skipReasonIcon} />
                  <span className={styles.skipReasonLabel}>{reason.label}</span>
                </label>
              );
            })}
          </div>

          {isOther ? (
            <textarea
              className={styles.skipOtherInput}
              name="skipReasonDetail"
              placeholder="Please share additional information"
              value={additionalInfo}
              onChange={(event) => setAdditionalInfo(event.target.value)}
              rows={3}
            />
          ) : null}
        </div>

        <div className={styles.confirmModalActions}>
          <button
            type="button"
            className={styles.confirmCancelButton}
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmSubmitButton}
            disabled={!canConfirm || isPending}
            onClick={() =>
              onConfirm({
                skipReasonCode: reasonCode,
                skipReasonDetail: isOther ? additionalInfo.trim() : "",
              })
            }
          >
            {isPending ? "Saving..." : "Confirm Skip"}
          </button>
        </div>
      </div>
    </MuiDialog>
  );
};

export default SkipBatchModal;
